<template>
    <Head>
        <title>{{ podcast.title }}</title>
    </Head>

    <frontend-layout>
        <template #page_header>
            <div
                class="flex justify-center gap-4 bg-gray-100 p-4 text-center text-gray-800"
            >
                <span>1 episode = 1 hour of continuing education credits</span>

                <emtpro-explainer-modal />
            </div>
        </template>

        <div class="mx-auto px-4 pt-12 lg:container lg:px-0">
            <div class="flex flex-col lg:flex-row lg:gap-20">
                <div>
                    <div class="mb-4 flex gap-4 border-b pb-4">
                        <Link
                            :href="route('frontend.podcasts.index')"
                            class="text-[16px] text-gray-600 hover:cursor-pointer hover:text-gray-800"
                            >Back to all episodes
                        </Link>
                    </div>
                    <div class="flex">
                        <frontend-header-h1 :text="podcast.title" />
                    </div>
                    <div class="my-4 flex gap-4">
                        <div
                            class="whitespace-nowrap text-[16px] text-gray-600"
                        >
                            {{
                                dayjs(podcast.updated_at)
                                    .utc(true)
                                    .tz($page.props.display_timezone)
                                    .format($page.props.date_formats.short_nice)
                            }}
                        </div>
                        <div class="border-l border-gray-200">&nbsp;</div>
                        <div class="text-[16px] text-gray-600">
                            <media-duration
                                format="full"
                                :duration-in-seconds="podcast.duration"
                            />
                        </div>
                        <div class="flex gap-2 text-[16px]">
                            <span
                                v-for="category in podcast.categories"
                                class="rounded bg-gray-100 p-2 py-1 text-gray-700"
                            >
                                {{ category.title }}
                            </span>
                        </div>
                    </div>

                    <div class="flex items-center gap-4 py-2 text-[16px]">
                        <podcast-player :podcast="podcast"></podcast-player>

                        <a
                            :href="
                                'https://content.jwplatform.com/videos/' +
                                podcast.token +
                                '-tsPSRrTH.aac'
                            "
                            class="flex items-center gap-2 rounded rounded-full px-3 py-2 text-primary-500 hover:cursor-pointer hover:text-primary-400"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clip-path="url(#clip0_1721_5336)">
                                    <path
                                        d="M6.66699 14.168L10.0003 17.5013L13.3337 14.168"
                                        stroke="#2475C7"
                                        stroke-width="1.66667"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M10 10V17.5"
                                        stroke="#2475C7"
                                        stroke-width="1.66667"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M17.4003 15.0769C18.1248 14.5674 18.6681 13.8404 18.9514 13.0012C19.2347 12.162 19.2431 11.2544 18.9756 10.4101C18.7081 9.56576 18.1785 8.82864 17.4637 8.3057C16.7489 7.78276 15.886 7.5012 15.0003 7.5019H13.9503C13.6997 6.52512 13.2307 5.61794 12.5787 4.84863C11.9268 4.07932 11.1088 3.46795 10.1863 3.06053C9.26387 2.6531 8.26101 2.46025 7.25325 2.49649C6.24548 2.53274 5.25907 2.79712 4.36827 3.26975C3.47747 3.74238 2.7055 4.41094 2.11047 5.22509C1.51544 6.03924 1.11285 6.97777 0.933028 7.97002C0.753202 8.96227 0.800819 9.98239 1.07229 10.9536C1.34377 11.9248 1.83203 12.8217 2.50031 13.5769"
                                        stroke="#2475C7"
                                        stroke-width="1.66667"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1721_5336">
                                        <rect
                                            width="20"
                                            height="20"
                                            fill="white"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <span>Download</span>
                        </a>
                    </div>
                    <div class="" v-if="podcast.show_notes">
                        <div class="text-[24px] font-semibold text-gray-900">
                            Show Notes
                        </div>
                        <div
                            class="mb-8 mt-4 text-[16px] leading-loose text-gray-800"
                        >
                            <div class="cms-wysiwyg-content">
                                <span v-html="podcast.show_notes"></span>
                            </div>
                        </div>
                    </div>
                    <div class="" v-if="podcast.downloads">
                        <div class="text-[20px] font-semibold text-gray-900">
                            Downloads
                        </div>
                        <div
                            class="mb-8 mt-4 text-[16px] leading-loose text-gray-800"
                        >
                            <ul>
                                <li
                                    v-for="download in podcast.downloads"
                                    class="mb-1"
                                >
                                    <a
                                        :href="
                                            '/storage/public/' +
                                            download.file_src
                                        "
                                        target="_blank"
                                        class="link flex items-center gap-2 !font-medium"
                                    >
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g
                                                clip-path="url(#clip0_1721_5336)"
                                            >
                                                <path
                                                    d="M6.66699 14.168L10.0003 17.5013L13.3337 14.168"
                                                    stroke="#2475C7"
                                                    stroke-width="1.66667"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                                <path
                                                    d="M10 10V17.5"
                                                    stroke="#2475C7"
                                                    stroke-width="1.66667"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                                <path
                                                    d="M17.4003 15.0769C18.1248 14.5674 18.6681 13.8404 18.9514 13.0012C19.2347 12.162 19.2431 11.2544 18.9756 10.4101C18.7081 9.56576 18.1785 8.82864 17.4637 8.3057C16.7489 7.78276 15.886 7.5012 15.0003 7.5019H13.9503C13.6997 6.52512 13.2307 5.61794 12.5787 4.84863C11.9268 4.07932 11.1088 3.46795 10.1863 3.06053C9.26387 2.6531 8.26101 2.46025 7.25325 2.49649C6.24548 2.53274 5.25907 2.79712 4.36827 3.26975C3.47747 3.74238 2.7055 4.41094 2.11047 5.22509C1.51544 6.03924 1.11285 6.97777 0.933028 7.97002C0.753202 8.96227 0.800819 9.98239 1.07229 10.9536C1.34377 11.9248 1.83203 12.8217 2.50031 13.5769"
                                                    stroke="#2475C7"
                                                    stroke-width="1.66667"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1721_5336">
                                                    <rect
                                                        width="20"
                                                        height="20"
                                                        fill="white"
                                                    />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <span>{{ download.file_title }}</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div
                    class="mx-auto mb-8 mt-8 flex w-full flex-none flex-col gap-8 md:w-auto md:flex-row lg:mt-0 lg:max-w-[312px] lg:flex-col"
                >
                    <div class="">
                        <div class="mb-4 text-[16px] text-gray-600">
                            Also available on
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <a
                                target="_blank"
                                href="https://podcasts.apple.com/us/podcast/emtpro-podcast/id1520031610"
                                class="flex items-center gap-2 rounded rounded-full bg-gray-200 px-4 py-2 text-[16px] text-gray-700 hover:cursor-pointer"
                            >
                                <span>Apple</span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.5 9.75V14.25C13.5 14.6478 13.342 15.0294 13.0607 15.3107C12.7794 15.592 12.3978 15.75 12 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V6C2.25 5.60218 2.40804 5.22064 2.68934 4.93934C2.97064 4.65804 3.35218 4.5 3.75 4.5H8.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.25 2.25H15.75V6.75"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M7.5 10.5L15.75 2.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </a>

                            <a
                                href="https://www.stitcher.com/show/emtpro-podcast"
                                target="_blank"
                                class="flex items-center gap-2 rounded rounded-full bg-gray-200 px-4 py-2 text-[16px] text-gray-700 hover:cursor-pointer"
                            >
                                <span>Stitcher</span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.5 9.75V14.25C13.5 14.6478 13.342 15.0294 13.0607 15.3107C12.7794 15.592 12.3978 15.75 12 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V6C2.25 5.60218 2.40804 5.22064 2.68934 4.93934C2.97064 4.65804 3.35218 4.5 3.75 4.5H8.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.25 2.25H15.75V6.75"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M7.5 10.5L15.75 2.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </a>

                            <a
                                href="https://podcasts.google.com/feed/aHR0cHM6Ly9lbXRwcm8uY29tL2ZlZWQvcG9kY2FzdA"
                                target="_blank"
                                class="flex items-center gap-2 rounded rounded-full bg-gray-200 px-4 py-2 text-[16px] text-gray-700 hover:cursor-pointer"
                            >
                                <span>Google</span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.5 9.75V14.25C13.5 14.6478 13.342 15.0294 13.0607 15.3107C12.7794 15.592 12.3978 15.75 12 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V6C2.25 5.60218 2.40804 5.22064 2.68934 4.93934C2.97064 4.65804 3.35218 4.5 3.75 4.5H8.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.25 2.25H15.75V6.75"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M7.5 10.5L15.75 2.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </a>

                            <a
                                href="https://open.spotify.com/show/3ZfsPEkMBMBsuiHZCznP7R"
                                target="_blank"
                                class="flex items-center gap-2 rounded rounded-full bg-gray-200 px-4 py-2 text-[16px] text-gray-700 hover:cursor-pointer"
                            >
                                <span>Spotify</span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.5 9.75V14.25C13.5 14.6478 13.342 15.0294 13.0607 15.3107C12.7794 15.592 12.3978 15.75 12 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V6C2.25 5.60218 2.40804 5.22064 2.68934 4.93934C2.97064 4.65804 3.35218 4.5 3.75 4.5H8.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.25 2.25H15.75V6.75"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M7.5 10.5L15.75 2.25"
                                        stroke="#2475C7"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>

                    <div class="">
                        <emtprep-upsell-ad />
                    </div>
                    <div class="">
                        <emtce-promo-ad />
                    </div>
                </div>
            </div>
        </div>
    </frontend-layout>
</template>

<script>
import FrontendLayout from "@/_Layouts/FrontendLayout";
import EmtprepUpsellAd from "@/Components/EmtprepUpsellAd";
import EmtcePromoAd from "@/Components/EmtcePromoAd";
import FrontendHeaderH1 from "@/Components/FrontendHeaderH1";
import MediaDuration from "@/Components/MediaDuration";
import PodcastPlayer from "@/Components/PodcastPlayer";
import NewModal from "@/Components/NewModal";
import EmtproExplainerModal from "@/Pages/Frontend/EmtproExplainerModal";

export default {
    name: "PodcastShow",
    components: {
        EmtproExplainerModal,
        PodcastPlayer,
        FrontendHeaderH1,
        FrontendLayout,
        EmtprepUpsellAd,
        EmtcePromoAd,
        MediaDuration,
        NewModal,
    },
    props: {
        podcast: {
            type: Object,
            default: [],
        },
    },
    data() {
        return {};
    },
    methods: {},
};
</script>

<style scoped></style>
